import { MoreVertOutlined, NotificationsOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import { LocationNoteDto } from "service/api/estabrookSchemas";
import { makeSxStyles } from "ui";
import { convertIsoToFullDateAndTime } from "../../../utils/date-time";
import EditNote from "./edit-note";

type CustomerNoteProps = {
  onDelete: () => void;
  onBellClick: () => void;
  onContentEdit: (newContent: string) => void;
  note: LocationNoteDto;
  isEditNotePending?: boolean;
};

const CustomerNote = ({
  note,
  onDelete,
  onContentEdit,
  onBellClick,
  isEditNotePending,
}: CustomerNoteProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const open = Boolean(anchorEl);

  const styles = useStyles();
  const theme = useTheme();
  const { palette } = theme;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = () => {
    handleClose();
    setIsEditing(true);
  };

  const handleDelete = () => {
    handleClose();
    onDelete();
  };

  return (
    <Box sx={styles.note}>
      <Box sx={styles.flexBetween}>
        <Typography
          variant="labelLarge"
          color={palette.grey[200]}
          fontWeight={500}
        >
          Note
        </Typography>
        <Box sx={styles.noteActions}>
          <Button
            sx={styles.actionButton}
            onClick={onBellClick}
            disabled={isEditNotePending}
          >
            <NotificationsOutlined
              sx={{
                fontSize: "16px",
                color: note.mustRead ? palette.success.main : palette.grey[200],
              }}
            />
          </Button>
          <Button
            sx={styles.actionButton}
            aria-controls={open ? "note-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
            onClick={handleClick}
          >
            <MoreVertOutlined
              sx={{ fontSize: "16px", color: palette.grey[200] }}
            />
          </Button>
          <Menu
            id="note-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              "aria-labelledby": "basic-button",
            }}
          >
            <MenuItem sx={{ width: "200px" }} onClick={handleEditClick}>
              Edit Note
            </MenuItem>
            <MenuItem sx={{ width: "200px" }} onClick={handleDelete}>
              Delete Note
            </MenuItem>
          </Menu>
        </Box>
      </Box>
      <Box sx={styles.noteBody}>
        <Typography
          variant="titleMedium"
          color={palette.grey[100]}
          sx={styles.noteContent}
        >
          {note.content}
        </Typography>

        <Typography
          variant="labelMedium"
          color={palette.grey[300]}
          sx={styles.noteMetadata}
        >
          {convertIsoToFullDateAndTime(note.createdAt)} by{" "}
          {note.adminId ? note.admin.email : note.driver.email}
        </Typography>
      </Box>

      <EditNote
        open={isEditing}
        onClose={() => setIsEditing(false)}
        inputValue={note.content}
        onSave={(value) => {
          setIsEditing(false);
          onContentEdit(value || note.content);
        }}
      />
    </Box>
  );
};

const useStyles = makeSxStyles(({ palette: { grey } }) => ({
  note: {
    borderRadius: "8px",
    padding: "16px",
    backgroundColor: grey[700],
    minWidth: 0, // Allow shrinking
  },
  flexBetween: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  noteActions: {
    display: "flex",
    alignItems: "center",
  },
  noteBody: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    marginTop: "12px",
    minWidth: 0, // Allow shrinking
  },
  noteContent: {
    wordWrap: "break-word",
    overflowWrap: "break-word",
    wordBreak: "break-word",
    minWidth: 0, // Allow shrinking
  },
  noteMetadata: {
    wordWrap: "break-word",
    overflowWrap: "break-word",
    minWidth: 0, // Allow shrinking
  },
  actionButton: {
    minWidth: "28px",
    maxWidth: "28px",
    height: "28px",
    display: "flex",
    alignItems: "center",

    "&:disabled": {
      opacity: 0.5,
    },
  },
}));

export default CustomerNote;
